import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/app/components/ui/Badge';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Skeleton } from '@/app/components/ui/Skeleton';
import { formatDate, formatNumber, formatShortDate } from '@/lib/format';
import { useThemeColors } from '@/lib/useThemeColors';
import type { CardData } from '@/features/vitality/deriveCards';
import type { DailyPoint } from '@/features/vitality/series';
import {
  CARD_BADGE,
  LOWER_IS_BETTER,
  cardStroke,
  statusPillFor,
  trendDirection,
  type CardId,
} from '@/features/vitality/metrics';
import { StatusNote } from '@/features/statistics/StatsPanel';

/** One "value" line per card — sleep is minutes-of-day, the rest are a
 * single number with a unit suffix from `vitality.cards.<id>.unit`. */
function formatValue(cardId: CardId, value: number, t: TFunction): string {
  if (cardId === 'sleep') {
    const totalMinutes = Math.round(value / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return t('vitality.units.hoursMinutes', { h: hours, m: minutes });
  }
  const rounded = Number.isInteger(value) ? value : Math.round(value * 10) / 10;
  const unit = t(`vitality.cards.${cardId}.unit`);
  // No space before a percentage sign ("69%", not "69 %") — every other
  // unit keeps the space ("9,000 steps/day", "51 ms").
  return unit === '%' ? `${formatNumber(rounded)}%` : `${formatNumber(rounded)} ${unit}`;
}

/** The series' own mean, for the "gem. X" figure and the current value's
 * comparison against it (`AverageDelta` below) — kept separate from
 * `averageOf`'s formatted string so both can share one computed number. */
function seriesMean(series: DailyPoint[] | undefined): number | null {
  if (!series || series.length === 0) return null;
  const known = series.filter((p): p is DailyPoint & { value: number } => p.value !== null);
  if (known.length === 0) return null;
  return known.reduce((sum, p) => sum + p.value, 0) / known.length;
}

function averageOf(mean: number | null, cardId: CardId, t: TFunction): string | null {
  if (mean === null) return null;
  return t('vitality.units.average', { value: formatValue(cardId, mean, t) });
}

/** The signed "(+3)"/"(-2)" next to "gem. X" — how the current value compares
 * to its own period average, distinct from `TrendIndicator`'s "vs last week"
 * figure (that one compares against the *previous period*; this one against
 * *this period's own mean*). Color follows the same good/bad-direction rule
 * as `TrendIndicator`. Renders nothing when there's no average to compare
 * against, or the value sits within rounding of it. */
function AverageDelta({
  cardId,
  value,
  mean,
}: {
  cardId: CardId;
  value: number;
  mean: number | null;
}) {
  const { t } = useTranslation();
  if (mean === null) return null;
  const change = value - mean;
  if (Math.abs(change) < 0.01) return null;
  const improved = LOWER_IS_BETTER[cardId] ? change < 0 : change > 0;
  const rounded = Math.abs(Math.round(change * 10) / 10);
  if (rounded === 0) return null;
  return (
    <span
      className={improved ? 'text-mint-deep text-[13px]' : 'text-danger text-[13px]'}
      title={t('vitality.trend.vsAverage')}
    >
      ({change > 0 ? '+' : '-'}
      {formatNumber(rounded)})
    </span>
  );
}

/** The compact "Δ wk" figure next to the big number, and the short
 * categorical label the Niveau/Trend/Actie row shows for the same change. */
function TrendIndicator({
  cardId,
  value,
  previous,
}: {
  cardId: CardId;
  value: number;
  previous: number | null;
}) {
  const { t } = useTranslation();
  if (previous === null) return null;
  const change = value - previous;
  if (Math.abs(change) < 0.01) {
    return <span className="text-ink text-[13px]">{t('vitality.trend.flat')}</span>;
  }
  const improved = LOWER_IS_BETTER[cardId] ? change < 0 : change > 0;
  const arrow = change > 0 ? '↗' : '↘';
  return (
    <span
      className={improved ? 'text-mint-deep text-[13px]' : 'text-danger text-[13px]'}
      title={t('vitality.trend.vsPrevious')}
    >
      {arrow} {change > 0 ? '+' : '-'}
      {formatNumber(Math.abs(Math.round(change * 10) / 10))} {t('vitality.trend.week')}
    </span>
  );
}

function trendCellLabel(cardId: CardId, change: number | null, t: TFunction): string {
  const direction = trendDirection(change);
  if (direction === 'flat') return t('vitality.trend.flat');
  const improved = LOWER_IS_BETTER[cardId] ? direction === 'down' : direction === 'up';
  if (improved) return t('vitality.trend.up');
  return LOWER_IS_BETTER[cardId] ? t('vitality.trend.upBad') : t('vitality.trend.down');
}

function Sparkline({ cardId, series }: { cardId: CardId; series: DailyPoint[] }) {
  const colors = useThemeColors();
  const stroke = cardStroke(colors, cardId);
  const data = series.map((p) => ({ ...p }));

  if (data.every((p) => p.value === null)) return null;

  return (
    <div className="h-28 w-full" data-testid={`vitality-sparkline-${cardId}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0 }}>
          <defs>
            <linearGradient id={`vitality-fill-${cardId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.25} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={`${colors.ink}1a`} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: colors.ink }}
            tickFormatter={(value: unknown) => formatShortDate(String(value))}
            interval="preserveStartEnd"
            axisLine={false}
            tickLine={false}
          />
          <YAxis tick={{ fontSize: 11, fill: colors.ink }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{
              background: 'var(--paper)',
              border: '1px solid var(--ink)',
              borderRadius: 12,
              fontSize: 12,
            }}
            labelFormatter={(value: unknown) => formatDate(String(value))}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={stroke}
            fill={`url(#vitality-fill-${cardId})`}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * One Vitality card: colored value + sparkline, "Gemeten"/"AI-inzicht"
 * badge plus a friendlier status pill, then a Niveau/Trend/Actie strip and
 * a one-line HR-action suggestion. `status` mirrors the batch panel's own
 * outcome; `data === undefined` is the loading state.
 */
export function VitalityCard({
  cardId,
  status,
  data,
  series,
  departmentId,
}: {
  cardId: CardId;
  status: 'ok' | 'forbidden' | 'insufficient_data';
  data?: CardData | undefined;
  series?: DailyPoint[] | undefined;
  /** The page's currently-selected department, if any (`scope: 'me'` has
   * none) — the "Actie" cell links to that department's own page (members,
   * stats, settings), the one place an HR-admin/coach can actually act on
   * what a card is showing, rather than a "N.t.b." stand-in for a
   * notify/escalate feature that doesn't exist yet. */
  departmentId?: string | undefined;
}) {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const badge = CARD_BADGE[cardId];
  const stroke = cardStroke(colors, cardId);

  const direction = data
    ? trendDirection(
        data.value !== null && data.previous !== null ? data.value - data.previous : null,
      )
    : 'flat';
  const pill = data ? statusPillFor(cardId, data.level, direction) : null;
  const mean = seriesMean(series);
  const averageText = averageOf(mean, cardId, t);

  return (
    <Card data-testid={`vitality-card-${cardId}`}>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <span
              className="inline-block size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: stroke }}
              aria-hidden="true"
            />
            {t(`vitality.cards.${cardId}.title`)}
          </span>
        }
        info={t(`vitality.cards.${cardId}.info`)}
        action={
          <span className="flex flex-col items-end gap-1">
            <Badge tone={badge === 'measured' ? 'mint' : 'outline'} className="whitespace-nowrap">
              {t(`vitality.badge.${badge}`)}
            </Badge>
            {pill && (
              <Badge
                tone={pill === 'opportunity' ? 'warning' : 'mint'}
                className="whitespace-nowrap"
              >
                {t(`vitality.statusPill.${pill}`)}
              </Badge>
            )}
          </span>
        }
      />
      {status !== 'ok' ? (
        <StatusNote status={status} />
      ) : data === undefined ? (
        <Skeleton kind="card" />
      ) : data.value === null ? (
        <p className="text-ink text-sm">{t('vitality.status.noData')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-heading text-ink text-2xl font-semibold">
              {formatValue(cardId, data.value, t)}
            </span>
            <TrendIndicator cardId={cardId} value={data.value} previous={data.previous} />
            {averageText && (
              <span className="text-accent flex items-center gap-1 text-[13px]">
                {averageText}
                <AverageDelta cardId={cardId} value={data.value} mean={mean} />
              </span>
            )}
          </div>

          {series && <Sparkline cardId={cardId} series={series} />}

          <div className="border-line grid grid-cols-3 gap-2 rounded-xl border px-3 py-2 text-center">
            <div className="flex flex-col gap-0.5">
              <span className="text-accent text-[11px]">{t('vitality.table.level')}</span>
              <span className="text-ink text-sm font-medium">
                {data.level ? t(`vitality.level.${data.level}`) : '—'}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-accent text-[11px]">{t('vitality.table.trend')}</span>
              <span className="text-ink text-sm font-medium">
                {trendCellLabel(
                  cardId,
                  data.value !== null && data.previous !== null ? data.value - data.previous : null,
                  t,
                )}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-accent text-[11px]">{t('vitality.table.action')}</span>
              {departmentId ? (
                <Link
                  to={`/departments/${departmentId}`}
                  className="text-mint-deep text-sm font-medium underline-offset-2 hover:underline"
                  title={t('vitality.table.actionLinkTitle')}
                >
                  {t('vitality.table.actionLink')}
                </Link>
              ) : (
                <span className="text-ink text-sm font-medium">
                  {t('vitality.table.actionNone')}
                </span>
              )}
            </div>
          </div>

          <p className="text-ink text-[13px]">
            → {t(`vitality.actions.${cardId}.${data.level ?? 'opportunity'}`)}
          </p>
        </div>
      )}
    </Card>
  );
}
